import { z } from "zod";
import type {
  ConversationReference,
  ConversationReferenceInput,
} from "./conversation-reference.js";
import { Id } from "./ids.js";

export const WorkspaceWidgetSchema = z.enum(["attention", "activity", "recent-files", "computers"]);
export const WorkspaceLinkSchema = z.object({
  id: z.string().min(1).max(100),
  name: z.string().trim().min(1).max(80),
  url: z
    .string()
    .url()
    .max(2048)
    .refine((value) => {
      const url = new URL(value);
      return url.protocol === "https:" && !url.username && !url.password;
    }, "Use an HTTPS address without credentials"),
  group: z.enum(["work", "ai"]).default("work"),
});
export const WorkspaceLayoutSchema = z
  .object({
    widgets: z
      .array(WorkspaceWidgetSchema)
      .max(4)
      .default(["attention", "activity", "recent-files"]),
    links: z.array(WorkspaceLinkSchema).max(40).default([]),
    apps: z.array(WorkspaceLinkSchema).max(30).optional(),
    hiddenLinks: z.array(z.string().max(2048)).max(60).default([]),
    pinnedComputers: z.array(z.string().max(100)).max(20).default([]),
  })
  .refine(
    (value) =>
      new Set(value.widgets).size === value.widgets.length &&
      new Set(value.links.map((link) => link.id)).size === value.links.length &&
      new Set((value.apps ?? []).map((app) => app.id)).size === (value.apps ?? []).length,
    "Remove duplicate items",
  );
export const WorkspacePreferencesSchema = z.object({
  revision: z.number().int().nonnegative(),
  layout: WorkspaceLayoutSchema,
});
export type WorkspaceLayout = z.infer<typeof WorkspaceLayoutSchema>;
export type WorkspacePreferences = z.infer<typeof WorkspacePreferencesSchema>;

export const WorkspaceActivityItemSchema = z.object({
  id: z.string(),
  botId: z.string(),
  botName: z.string(),
  botColor: z.string(),
  title: z.string(),
  status: z.string(),
  updatedAt: z.string(),
  createdAt: z.string(),
  completedAt: z.string().nullable(),
  computerId: z.string().nullable(),
  groupId: z.string().nullable(),
});
export const WorkspaceActivitySchema = z.object({
  items: z.array(WorkspaceActivityItemSchema),
  nextCursor: z.string().nullable(),
  counts: z.object({
    working: z.number(),
    queued: z.number(),
    attention: z.number(),
    failed: z.number(),
  }),
  checkedAt: z.string(),
});
export type WorkspaceActivity = z.infer<typeof WorkspaceActivitySchema>;
export type WorkspaceActivityItem = z.infer<typeof WorkspaceActivityItemSchema>;
export const WorkspaceActivityQuerySchema = z
  .object({
    filter: z.enum(["active", "attention", "completed", "failed", "all"]).default("active"),
    cursor: z.string().max(200).optional(),
  })
  .default({ filter: "active" });
export const WorkspaceComputerSchema = z.object({
  owner: z.boolean().optional(),
  id: z.string(),
  name: z.string(),
  kind: z.string(),
  state: z.string(),
  lastSeenAt: z.string().nullable(),
  bots: z.array(z.object({ id: z.string(), name: z.string(), color: z.string() })),
  device: z.boolean(),
  /**
   * Which installation a seat on one of the person's own computers belongs to, so a client can
   * tell this machine's seat from one on another of their machines. Absent for every other kind.
   */
  installId: z.string().nullable().optional(),
});
export type WorkspaceComputer = z.infer<typeof WorkspaceComputerSchema>;
/** Choices stay within the selected space; cloud sessions only use their assigned bots. */
export function computerConnectionBots(machine: WorkspaceComputer, inventory: WorkspaceComputer[]) {
  if (machine.owner) return [];
  const bots =
    machine.bots.length || !machine.device ? machine.bots : inventory.flatMap((item) => item.bots);
  return [...new Map(bots.map((bot) => [bot.id, bot])).values()];
}
export function computerStateLabel(
  machine: Pick<WorkspaceComputer, "device" | "state"> & Partial<Pick<WorkspaceComputer, "owner">>,
): string {
  if (machine.owner && machine.state === "stopped") return "Stopped";
  if (!machine.device && ["suspended", "stopped"].includes(machine.state)) return "Sleeping";
  return (
    (
      {
        running: "Ready",
        online: "Online",
        offline: "Offline",
        starting: "Starting",
        booting: "Starting",
        suspending: "Stopping",
        error: "Unavailable",
        failed: "Unavailable",
      } as Record<string, string>
    )[machine.state] ?? machine.state
  );
}
export const WorkspaceSearchHitSchema = z.object({
  id: z.string(),
  kind: z.enum([
    "bot",
    "file",
    "message",
    "routine",
    "link",
    "ticket",
    "mission",
    "app",
    "computer",
  ]),
  title: z.string(),
  subtitle: z.string(),
  botId: z.string().optional(),
  groupId: z.string().optional(),
  artifactId: z.string().optional(),
  messageId: z.string().optional(),
  missionId: z.string().optional(),
  ticketId: z.string().optional(),
  url: z.string().optional(),
  webPath: z.string().optional(),
  mobilePath: z.string().optional(),
});
export type WorkspaceSearchHit = z.infer<typeof WorkspaceSearchHitSchema>;

/** Saved versions only: transfer an identity, never credentials or unsaved document bytes. */
export const SavedFileTransferSchema = z.object({
  version: z.literal(1),
  artifactId: z.string().min(1).max(200),
  sourceSpaceId: z.string().min(1).max(200),
});
export type SavedFileTransfer = z.infer<typeof SavedFileTransferSchema>;

/** A user-selected file is staged in the recipient's space, read from its original space. */
export function fileChatDestination(
  artifactId: string,
  sourceSpaceId: string | null,
  recipient: { id: string; spaceId: string },
) {
  const query = new URLSearchParams({ attach: artifactId });
  if (sourceSpaceId) query.set("attachSpace", sourceSpaceId);
  return {
    spaceId: recipient.spaceId,
    webPath: `/app/${encodeURIComponent(recipient.id)}?${query}`,
    mobilePath: `/thread?${new URLSearchParams({ botId: recipient.id, spaceId: recipient.spaceId, ...Object.fromEntries(query) })}`,
  };
}

/** Shown when the source ticket is missing or the caller can no longer read it. */
export const TICKET_UNAVAILABLE = "Ticket unavailable";

/**
 * Identity of an internal project ticket. The notes stay in the source space.
 * `sourceSpaceId` is that space, never a grant for the recipient conversation.
 */
export const TicketReferenceSchema = z
  .object({
    ticketId: Id.max(200),
    sourceSpaceId: Id.max(200),
    ref: z.string().min(1).max(40),
    title: z.string().min(1).max(200),
  })
  .strict();
export type TicketReference = z.infer<typeof TicketReferenceSchema>;

/** A sent message keeps the same identity. `spaceId` is the ticket's source space. */
export const TicketMessageBlockSchema = z
  .object({
    kind: z.literal("ticket"),
    ticketId: Id.max(200),
    spaceId: Id.max(200),
    ref: z.string().min(1).max(40),
    title: z.string().min(1).max(200),
  })
  .strict();

const handoffIdentity = z.string().regex(/^[a-zA-Z0-9_-]{1,200}$/);

/** Stages the ticket in the recipient conversation. The address is consumed once and is not restored. */
export function ticketChatDestination(
  ticketId: string,
  sourceSpaceId: string,
  recipient: { id: string; spaceId: string },
) {
  const ticket = handoffIdentity.parse(ticketId);
  const source = handoffIdentity.parse(sourceSpaceId);
  const query = new URLSearchParams({ attachTicket: ticket, attachTicketSpace: source });
  return {
    spaceId: recipient.spaceId,
    webPath: `/app/${encodeURIComponent(recipient.id)}?${query}`,
    mobilePath: `/thread?${new URLSearchParams({ botId: recipient.id, spaceId: recipient.spaceId, ...Object.fromEntries(query) })}`,
  };
}

export function appendTicketReference<T extends TicketReference>(
  staged: readonly T[],
  next: TicketReference,
): readonly T[] | readonly TicketReference[] {
  const parsed = TicketReferenceSchema.parse(next);
  if (
    staged.some(
      (item) => item.ticketId === parsed.ticketId && item.sourceSpaceId === parsed.sourceSpaceId,
    )
  )
    return staged;
  return [...staged, parsed];
}

export function ticketMessageBlock(reference: TicketReference) {
  return TicketMessageBlockSchema.parse({
    kind: "ticket",
    ticketId: reference.ticketId,
    spaceId: reference.sourceSpaceId,
    ref: reference.ref,
    title: reference.title,
  });
}

/** Names the ticket for the recipient without copying its notes. */
export function ticketReferenceNote(
  reference: Pick<TicketReference, "ref" | "ticketId" | "sourceSpaceId">,
): string {
  return `Referenced ticket ${reference.ref} (${reference.ticketId}) from space ${reference.sourceSpaceId}.`;
}

/** Stages source identity in an existing recipient; never sends or copies content. */
export function conversationChatDestination(
  reference: ConversationReferenceInput,
  bot: { id: string; spaceId: string },
) {
  const query = new URLSearchParams({
    attachConversation: reference.botId,
    attachConversationSpace: reference.sourceSpaceId,
    ...(reference.threadId ? { attachConversationThread: reference.threadId } : {}),
    ...(reference.messageId ? { attachConversationMessage: reference.messageId } : {}),
  });
  return {
    spaceId: bot.spaceId,
    webPath: `/app/${encodeURIComponent(bot.id)}?${query.toString()}`,
    mobilePath: `/thread?botId=${encodeURIComponent(bot.id)}&spaceId=${encodeURIComponent(bot.spaceId)}&${query.toString()}`,
  };
}

/** Call threads.reference before opening. The source space is not an access grant. */
export function conversationReferencePaths(reference: ConversationReference) {
  return {
    ...workspaceResourcePaths({
      id: reference.botId,
      kind: reference.messageId ? "message" : "bot",
      title: reference.title,
      subtitle: "",
      botId: reference.botId,
      ...(reference.messageId ? { messageId: reference.messageId } : {}),
    })!,
    spaceId: reference.sourceSpaceId,
  };
}

/** UI search and platform tools share exact resource destinations, never session grants. */
export function workspaceResourcePaths(
  hit: WorkspaceSearchHit,
): { webPath: string; mobilePath: string } | null {
  const encoded = (value: string) => encodeURIComponent(value);
  if (hit.artifactId)
    return {
      webPath: `/app/artifacts/${encoded(hit.artifactId)}?version=${encoded(hit.artifactId)}`,
      mobilePath: `/artifact?artifactId=${encoded(hit.artifactId)}&version=${encoded(hit.artifactId)}`,
    };
  if (hit.missionId)
    return {
      webPath: `/app/missions?id=${encoded(hit.missionId)}`,
      mobilePath: `/missions?id=${encoded(hit.missionId)}`,
    };
  if (hit.ticketId)
    return {
      webPath: `/app/projects?ticket=${encoded(hit.ticketId)}`,
      mobilePath: `/project-ticket?ticketId=${encoded(hit.ticketId)}`,
    };
  if (hit.kind === "app") {
    const app = WORKSPACE_APPS.find((app) => app.id === hit.id);
    return app ? { webPath: app.web, mobilePath: app.mobile } : null;
  }
  if (hit.kind === "computer")
    return {
      webPath: `/app/workspace/computers?computer=${encoded(hit.id)}`,
      mobilePath: `/workspace/computers?computer=${encoded(hit.id)}`,
    };
  if (hit.groupId || hit.botId) {
    const id = encoded((hit.groupId ?? hit.botId)!);
    return {
      webPath: `/app/${hit.groupId ? "g/" : ""}${id}${hit.messageId ? `?m=${encoded(hit.messageId)}` : ""}`,
      mobilePath: `/${hit.groupId ? "group-thread?groupId" : "thread?botId"}=${id}${hit.messageId ? `&messageId=${encoded(hit.messageId)}` : ""}`,
    };
  }
  return null;
}

export const WORKSPACE_APPS = [
  {
    id: "tasks",
    name: "Tasks",
    description: "Work requested in your conversations",
    web: "/app/tasks",
    mobile: "/tasks",
  },
  {
    id: "missions",
    name: "Missions",
    description: "Outcomes, crew, evidence and decisions",
    web: "/app/missions",
    mobile: "/missions",
  },
  {
    id: "projects",
    name: "Projects",
    description: "Your private projects and planning board",
    web: "/app/projects",
    mobile: "/projects",
  },
  {
    id: "messages",
    name: "Messages",
    description: "Your agents and conversations",
    web: "/app/messages",
    mobile: "/messages",
  },
  {
    id: "activity",
    name: "Activity",
    description: "Work in progress and requests for you",
    web: "/app/workspace/activity",
    mobile: "/workspace/activity",
  },
  {
    id: "briefing",
    name: "Mission Briefing",
    description: "Tickets, pull requests and analytics",
    web: "/app/mission-briefing",
    mobile: "/mission-briefing",
  },
  {
    id: "files",
    name: "Files",
    description: "Project files and generated work",
    web: "/app/artifacts",
    mobile: "/artifacts",
  },
  {
    id: "editor",
    name: "Text Editor",
    description: "Write and edit documents",
    web: "/app/editor",
    mobile: "/editor",
  },
  {
    id: "computers",
    name: "Computers",
    description: "Cloud workspaces and connected devices",
    web: "/app/workspace/computers",
    mobile: "/workspace/computers",
  },
  {
    id: "system",
    name: "System",
    description: "Workspace capacity and service health",
    web: "/app/workspace/system",
    mobile: "/workspace/system",
  },
] as const;

export function activityLabel(status: string): string {
  return (
    (
      {
        queued: "Queued",
        leased: "Starting",
        running: "Working",
        waiting_input: "Needs you",
        waiting_takeover: "Needs control",
        completed: "Completed",
        failed: "Interrupted",
        cancelled: "Stopped",
      } as Record<string, string>
    )[status] ?? status
  );
}

export const WorkspaceHostHealthSchema = z.object({
  checkedAt: z.string().datetime(),
  cpuCount: z.number().positive(),
  load1: z.number().nonnegative(),
  memoryTotalBytes: z.number().nonnegative(),
  memoryAvailableBytes: z.number().nonnegative(),
  diskTotalBytes: z.number().nonnegative(),
  diskFreeBytes: z.number().nonnegative(),
  backup: z.object({
    status: z.enum(["success", "failed", "running", "unknown"]),
    completedAt: z.string().nullable(),
    restoreVerifiedAt: z.string().nullable(),
  }),
  services: z.array(z.object({ name: z.string(), state: z.string() })).max(30),
});
export const WorkspaceSystemSchema = z.object({
  checkedAt: z.string(),
  database: z.enum(["available", "unavailable"]),
  files: z.number(),
  bytes: z.number(),
  computers: z.number(),
  connectedDevices: z.number(),
  activeRuns: z.number(),
  oldestQueuedAt: z.string().nullable(),
  host: WorkspaceHostHealthSchema.nullable(),
});
export type WorkspaceSystem = z.infer<typeof WorkspaceSystemSchema>;

export const WORKSPACE_SYSTEM_SECTIONS = [
  { key: "overview", label: "Overview" },
  { key: "services", label: "Services" },
  { key: "recovery", label: "Recovery" },
  { key: "details", label: "Technical details" },
] as const;

export function workspaceResources(system: WorkspaceSystem) {
  const host = system.host;
  if (!host) return [];
  const gb = (value: number) => `${(value / 1024 ** 3).toFixed(1)} GB`;
  return [
    {
      name: "CPU load",
      value: host.load1.toFixed(2),
      detail: `${host.cpuCount} cores · 1 minute`,
      fraction: Math.min(1, host.load1 / host.cpuCount),
    },
    {
      name: "Memory",
      value: gb(host.memoryTotalBytes - host.memoryAvailableBytes),
      detail: `${gb(host.memoryTotalBytes)} total`,
      fraction: host.memoryTotalBytes ? 1 - host.memoryAvailableBytes / host.memoryTotalBytes : 0,
    },
    {
      name: "Storage",
      value: gb(host.diskTotalBytes - host.diskFreeBytes),
      detail: `${gb(host.diskTotalBytes)} total`,
      fraction: host.diskTotalBytes ? 1 - host.diskFreeBytes / host.diskTotalBytes : 0,
    },
  ];
}

/** The same health facts on every surface; absent/stale data never implies healthy. */
export function workspaceSystemRows(system: WorkspaceSystem): Array<[string, string]> {
  const bytes = (value: number) => `${(value / 1024 ** 3).toFixed(1)} GB`;
  const date = (value: string | null) =>
    value ? new Date(value).toLocaleString() : "Not recorded";
  const rows: Array<[string, string]> = [
    ["Database connection", system.database === "available" ? "Available" : "Unavailable"],
    ["Files, including versions", system.files.toLocaleString()],
    ["File storage", bytes(system.bytes)],
    ["Cloud computers", String(system.computers)],
    ["Online devices", String(system.connectedDevices)],
    ["Active runs", String(system.activeRuns)],
    ["Oldest queued task", system.oldestQueuedAt ? date(system.oldestQueuedAt) : "None"],
  ];
  const host = system.host;
  if (!host) return [...rows, ["Server telemetry", "Unavailable"]];
  rows.push(
    [
      "Server snapshot",
      `${date(host.checkedAt)}${Date.now() - Date.parse(host.checkedAt) > 300000 ? " · stale" : ""}`,
    ],
    ["CPU load · 1 minute", `${host.load1.toFixed(2)} / ${host.cpuCount} CPUs`],
    ["Available memory", `${bytes(host.memoryAvailableBytes)} / ${bytes(host.memoryTotalBytes)}`],
    ["Available storage", `${bytes(host.diskFreeBytes)} / ${bytes(host.diskTotalBytes)}`],
    ["Backup", host.backup.status],
    ["Last backup completed", date(host.backup.completedAt)],
    ["Last verified restore", date(host.backup.restoreVerifiedAt)],
  );
  for (const service of host.services) rows.push([service.name, service.state]);
  return rows;
}
